import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  List, Plus, Trash2, X, ChevronUp, ChevronDown, Search,
  Star, GripVertical, Loader2, AlertCircle, Eye, RefreshCw,
  TrendingUp, TrendingDown, Minus, FolderPlus, Check
} from 'lucide-react';
import { brokerAPI, Watchlist } from '../../api/broker';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SymbolQuote {
  price: number;
  change_pct: number;
  loading: boolean;
  error: boolean;
}

interface WatchlistManagerProps {
  onSymbolClick?: (symbol: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const WatchlistManager: React.FC<WatchlistManagerProps> = ({ onSymbolClick }) => {
  // ── State ──────────────────────────────────────────────────────────
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New watchlist creation
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const newNameRef = useRef<HTMLInputElement>(null);

  // Add symbol
  const [symbolInput, setSymbolInput] = useState('');
  const [addingSymbol, setAddingSymbol] = useState(false);

  // Delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Quotes cache  symbol -> SymbolQuote
  const [quotes, setQuotes] = useState<Record<string, SymbolQuote>>({});

  // Drag state
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // ── Derived ────────────────────────────────────────────────────────
  const selected = watchlists.find((w) => w.watchlist_id === selectedId) ?? null;

  // ── Fetch watchlists ──────────────────────────────────────────────
  const fetchWatchlists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await brokerAPI.getWatchlists();
      setWatchlists(res.watchlists);
      // Auto-select first if nothing selected or selected is gone
      if (
        res.watchlists.length > 0 &&
        (!selectedId || !res.watchlists.find((w) => w.watchlist_id === selectedId))
      ) {
        setSelectedId(res.watchlists[0].watchlist_id);
      }
      if (res.watchlists.length === 0) setSelectedId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load watchlists');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    fetchWatchlists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch quotes for selected watchlist symbols ───────────────────
  useEffect(() => {
    if (!selected || selected.symbols.length === 0) return;

    const symbolsToFetch = selected.symbols.filter(
      (s) => !quotes[s] || quotes[s].error
    );
    if (symbolsToFetch.length === 0) return;

    // Mark loading
    setQuotes((prev) => {
      const next = { ...prev };
      symbolsToFetch.forEach((s) => {
        next[s] = { price: 0, change_pct: 0, loading: true, error: false };
      });
      return next;
    });

    symbolsToFetch.forEach(async (symbol) => {
      try {
        const q = await brokerAPI.getQuote(symbol);
        const price = q.last?.price ?? q.trade?.p ?? q.price ?? 0;
        const prevClose = q.prev_close ?? q.dailyBar?.c ?? 0;
        const change_pct =
          prevClose > 0 ? ((price - prevClose) / prevClose) * 100 : 0;
        setQuotes((prev) => ({
          ...prev,
          [symbol]: { price, change_pct, loading: false, error: false },
        }));
      } catch {
        setQuotes((prev) => ({
          ...prev,
          [symbol]: { price: 0, change_pct: 0, loading: false, error: true },
        }));
      }
    });
  }, [selected, quotes]);

  // ── Focus the new-name input when opening ─────────────────────────
  useEffect(() => {
    if (creatingNew) newNameRef.current?.focus();
  }, [creatingNew]);

  // ── Create watchlist ──────────────────────────────────────────────
  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const res = await brokerAPI.createWatchlist(name);
      const created: Watchlist = res.watchlist ?? res;
      setWatchlists((prev) => [...prev, created]);
      setSelectedId(created.watchlist_id);
      setNewName('');
      setCreatingNew(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create watchlist');
    }
  };

  // ── Delete watchlist ──────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    try {
      await brokerAPI.deleteWatchlist(id);
      setWatchlists((prev) => prev.filter((w) => w.watchlist_id !== id));
      if (selectedId === id) {
        const remaining = watchlists.filter((w) => w.watchlist_id !== id);
        setSelectedId(remaining.length > 0 ? remaining[0].watchlist_id : null);
      }
      setConfirmDeleteId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete watchlist');
    }
  };

  // ── Add symbol ────────────────────────────────────────────────────
  const handleAddSymbol = async () => {
    if (!selected) return;
    const sym = symbolInput.trim().toUpperCase();
    if (!sym) return;
    if (selected.symbols.includes(sym)) {
      setSymbolInput('');
      return;
    }
    setAddingSymbol(true);
    try {
      await brokerAPI.addSymbolToWatchlist(selected.watchlist_id, sym);
      setWatchlists((prev) =>
        prev.map((w) =>
          w.watchlist_id === selected.watchlist_id
            ? { ...w, symbols: [...w.symbols, sym] }
            : w
        )
      );
      setSymbolInput('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add symbol');
    } finally {
      setAddingSymbol(false);
    }
  };

  // ── Remove symbol ─────────────────────────────────────────────────
  const handleRemoveSymbol = async (symbol: string) => {
    if (!selected) return;
    try {
      await brokerAPI.removeSymbolFromWatchlist(selected.watchlist_id, symbol);
      setWatchlists((prev) =>
        prev.map((w) =>
          w.watchlist_id === selected.watchlist_id
            ? { ...w, symbols: w.symbols.filter((s) => s !== symbol) }
            : w
        )
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to remove symbol');
    }
  };

  // ── Reorder helpers ───────────────────────────────────────────────
  const moveSymbol = async (fromIdx: number, toIdx: number) => {
    if (!selected) return;
    const symbols = [...selected.symbols];
    const [moved] = symbols.splice(fromIdx, 1);
    symbols.splice(toIdx, 0, moved);
    // Optimistic update
    setWatchlists((prev) =>
      prev.map((w) =>
        w.watchlist_id === selected.watchlist_id ? { ...w, symbols } : w
      )
    );
    try {
      await brokerAPI.updateWatchlist(selected.watchlist_id, { symbols });
    } catch {
      // Revert on failure
      fetchWatchlists();
    }
  };

  // Drag handlers
  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const handleDrop = (idx: number) => {
    if (dragIdx !== null && dragIdx !== idx) {
      moveSymbol(dragIdx, idx);
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  // ── Format helpers ────────────────────────────────────────────────
  const fmtPrice = (v: number) =>
    v >= 1 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `$${v.toFixed(6)}`;
  const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

  // ── Render ────────────────────────────────────────────────────────

  /* Loading / Error splash */
  if (loading && watchlists.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading watchlists...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900 text-gray-100 rounded-lg border border-gray-700/50 overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800/60 border-b border-gray-700/50">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-200">
          <Star className="w-4 h-4 text-yellow-400" />
          Watchlists
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => fetchWatchlists()}
            title="Refresh"
            className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => { setCreatingNew(true); setNewName(''); }}
            title="New Watchlist"
            className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-emerald-400 transition-colors"
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── Error banner ───────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-900/30 border-b border-red-700/40 text-red-300 text-xs">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* ── New Watchlist inline form ──────────────────────────────── */}
      {creatingNew && (
        <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-800/40 border-b border-gray-700/40">
          <input
            ref={newNameRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') setCreatingNew(false);
            }}
            placeholder="Watchlist name..."
            className="flex-1 bg-gray-700/60 border border-gray-600 rounded px-2 py-1 text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={handleCreate}
            disabled={!newName.trim()}
            className="p-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setCreatingNew(false)}
            className="p-1 rounded hover:bg-gray-700 text-gray-400"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Watchlist tabs ─────────────────────────────────────────── */}
      {watchlists.length > 0 && (
        <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-800/30 border-b border-gray-700/40 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-700">
          {watchlists.map((wl) => (
            <div key={wl.watchlist_id} className="relative flex items-center group">
              <button
                onClick={() => setSelectedId(wl.watchlist_id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedId === wl.watchlist_id
                    ? 'bg-blue-600/30 text-blue-300 border border-blue-500/40'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50 border border-transparent'
                }`}
              >
                <List className="w-3 h-3" />
                {wl.name}
                <span className="text-[10px] opacity-60 ml-0.5">
                  {wl.symbols.length}
                </span>
              </button>
              {/* Delete button on hover */}
              {confirmDeleteId === wl.watchlist_id ? (
                <div className="absolute -right-1 -top-1 z-10 flex items-center gap-0.5 bg-gray-800 border border-red-600/50 rounded px-1 py-0.5 shadow-lg">
                  <button
                    onClick={() => handleDelete(wl.watchlist_id)}
                    className="text-[10px] text-red-400 hover:text-red-300 font-medium px-1"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="text-[10px] text-gray-400 hover:text-gray-200 px-0.5"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDeleteId(wl.watchlist_id)}
                  className="absolute -right-1 -top-1 hidden group-hover:flex items-center justify-center w-4 h-4 rounded-full bg-gray-700 hover:bg-red-600 text-gray-400 hover:text-white transition-colors shadow"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Empty: no watchlists at all ────────────────────────────── */}
      {watchlists.length === 0 && !loading && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10 text-gray-500">
          <Star className="w-8 h-8 text-gray-600" />
          <p className="text-sm">No watchlists yet</p>
          <button
            onClick={() => { setCreatingNew(true); setNewName(''); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Create Watchlist
          </button>
        </div>
      )}

      {/* ── Selected watchlist content ─────────────────────────────── */}
      {selected && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Add symbol row */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-gray-700/40 bg-gray-800/20">
            <Search className="w-3.5 h-3.5 text-gray-500" />
            <input
              value={symbolInput}
              onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddSymbol();
              }}
              placeholder="Add symbol (e.g. AAPL)..."
              disabled={addingSymbol}
              className="flex-1 bg-transparent text-xs text-gray-100 placeholder-gray-500 focus:outline-none"
            />
            {symbolInput.trim() && (
              <button
                onClick={handleAddSymbol}
                disabled={addingSymbol}
                className="p-0.5 rounded hover:bg-gray-700 text-emerald-400 disabled:opacity-50"
              >
                {addingSymbol ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
              </button>
            )}
          </div>

          {/* Symbol list */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700">
            {selected.symbols.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-gray-500">
                <Eye className="w-6 h-6 text-gray-600" />
                <p className="text-xs">No symbols in this watchlist</p>
                <p className="text-[10px] text-gray-600">
                  Type a symbol above and press Enter
                </p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-700/40">
                    <th className="w-6" />
                    <th className="text-left py-1.5 px-2 font-medium">Symbol</th>
                    <th className="text-right py-1.5 px-2 font-medium">Price</th>
                    <th className="text-right py-1.5 px-2 font-medium">Chg %</th>
                    <th className="w-16" />
                  </tr>
                </thead>
                <tbody>
                  {selected.symbols.map((symbol, idx) => {
                    const q = quotes[symbol];
                    const isUp = q && q.change_pct > 0;
                    const isDown = q && q.change_pct < 0;
                    const isDragging = dragIdx === idx;
                    const isDragOver = dragOverIdx === idx;

                    return (
                      <tr
                        key={symbol}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={() => handleDrop(idx)}
                        onDragEnd={handleDragEnd}
                        className={`border-b border-gray-800/40 transition-colors ${
                          isDragging
                            ? 'opacity-40'
                            : isDragOver
                            ? 'bg-blue-900/20 border-blue-500/30'
                            : 'hover:bg-gray-800/40'
                        }`}
                      >
                        {/* Drag handle */}
                        <td className="pl-1.5 py-1.5">
                          <div className="flex flex-col items-center gap-0">
                            <button
                              onClick={() => idx > 0 && moveSymbol(idx, idx - 1)}
                              disabled={idx === 0}
                              className="text-gray-600 hover:text-gray-300 disabled:opacity-20 disabled:cursor-default"
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <GripVertical className="w-3 h-3 text-gray-600 cursor-grab" />
                            <button
                              onClick={() =>
                                idx < selected.symbols.length - 1 &&
                                moveSymbol(idx, idx + 1)
                              }
                              disabled={idx === selected.symbols.length - 1}
                              className="text-gray-600 hover:text-gray-300 disabled:opacity-20 disabled:cursor-default"
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>
                        </td>

                        {/* Symbol & Name */}
                        <td className="py-1.5 px-2">
                          <button
                            onClick={() => onSymbolClick?.(symbol)}
                            className="text-left group"
                          >
                            <span className="font-semibold text-gray-100 group-hover:text-blue-400 transition-colors">
                              {symbol}
                            </span>
                          </button>
                        </td>

                        {/* Price */}
                        <td className="text-right py-1.5 px-2 tabular-nums text-gray-300">
                          {q?.loading ? (
                            <Loader2 className="w-3 h-3 animate-spin ml-auto text-gray-500" />
                          ) : q?.error ? (
                            <span className="text-gray-600">--</span>
                          ) : q ? (
                            fmtPrice(q.price)
                          ) : (
                            <span className="text-gray-600">--</span>
                          )}
                        </td>

                        {/* Change % */}
                        <td className="text-right py-1.5 px-2 tabular-nums">
                          {q?.loading ? (
                            <span className="text-gray-600">--</span>
                          ) : q && !q.error ? (
                            <span
                              className={`inline-flex items-center gap-0.5 ${
                                isUp
                                  ? 'text-emerald-400'
                                  : isDown
                                  ? 'text-red-400'
                                  : 'text-gray-400'
                              }`}
                            >
                              {isUp ? (
                                <TrendingUp className="w-3 h-3" />
                              ) : isDown ? (
                                <TrendingDown className="w-3 h-3" />
                              ) : (
                                <Minus className="w-3 h-3" />
                              )}
                              {fmtPct(q.change_pct)}
                            </span>
                          ) : (
                            <span className="text-gray-600">--</span>
                          )}
                        </td>

                        {/* Remove */}
                        <td className="text-right pr-2 py-1.5">
                          <button
                            onClick={() => handleRemoveSymbol(symbol)}
                            title={`Remove ${symbol}`}
                            className="p-0.5 rounded hover:bg-red-900/40 text-gray-600 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchlistManager;
